import { Inject, Injectable } from '@angular/core';

import { OfficeHomePageErrorHandler } from '../../error-handler/error-handler.service';
import { ErrorId } from '../../error-handler/error-id';
import { OfficeHomeApiConfigService } from '../../shared/services/officehome-api-config.service';
import { WindowService } from '../../shared/services/window.service';
import { ClientStartupConfigService } from '../../shared/shared-context/client-startup-config.service';
import { ContextService } from '../../shared/shared-context/context.service';
import { SERVICE_WORKER_ENABLED } from '../service-worker-enabled.injection-token';
import { SERVICE_WORKER_USER_ID_FACTORY } from '../service-worker-user-id.injection-token';
import { AuthorizedWarmupMessage, AuthorizedWarmupMessagePayloadV3, ServiceWorkerAuthorizedInstallService } from './service-worker-authorized-install.service';

/**
 * Responsible for providing the Office.com Installation Signal to the installing worker for Office.com so that Service Worker can complete installation
 */
@Injectable()
export class OfficeHomeServiceWorkerAuthorizedInstallService extends ServiceWorkerAuthorizedInstallService {
    private messageSent = false;

    constructor (
        private readonly errorHandler: OfficeHomePageErrorHandler,
        private readonly contextService: ContextService,
        @Inject(SERVICE_WORKER_ENABLED) private readonly serviceWorkerEnabled: boolean,
        @Inject(SERVICE_WORKER_USER_ID_FACTORY) private readonly serviceWorkerUserIdFactory: () => Promise<string>,
        windowService: WindowService,
        private readonly officeHomeApiConfigService: OfficeHomeApiConfigService,
        private readonly clientStartupConfigService: ClientStartupConfigService
    ) {
        super(windowService);
    }

    public async handleRegistration(registration: ServiceWorkerRegistration): Promise<void> {
        if (!this.serviceWorkerEnabled) {
            return;
        }

        try {
            const serviceWorkerIntervalUpdateEnabled = this.contextService.sharedContext.serviceWorkerIntervalUpdateEnabled;

            const postInstallMessageOnUpdate = () => {
                registration.addEventListener('updatefound', () => {
                    this.postAuthorizedInstallMessage(registration.installing);
                });
            };

            if (registration.installing) {
                this.postAuthorizedInstallMessage(registration.installing);
                if (serviceWorkerIntervalUpdateEnabled) {
                    postInstallMessageOnUpdate();
                }
            } else {
                postInstallMessageOnUpdate();
            }
        } catch (ex) {
            this.errorHandler.handleError(ex, ErrorId.OGBGO);
        }
    }

    private async postAuthorizedInstallMessage(installingWorker: ServiceWorker) {
        // Only send the install payload once per app boot.
        if (this.messageSent) {
            return;
        }

        const payload: AuthorizedWarmupMessagePayloadV3 = {
            token: null, // No token needed for Office.com
            userId: await this.serviceWorkerUserIdFactory(),
            apiPath: 'api/shellandconfigdata',
            apiHeaders: {
                [this.officeHomeApiConfigService.config.userIdHeader]: this.clientStartupConfigService.config.puid
            }
        };

        if (this.contextService.sharedContext.inMockMode) {
            this.addParamFromWindow(payload, 'mockauth');
            this.addParamFromWindow(payload, 'envType');
        }

        this.addParamFromWindow(payload, 'flight');

        const message: AuthorizedWarmupMessage = {
            type: 'OFFICECOM_AUTH_WARMUP',
            meta: 'installation-credentials',
            payload
        };

        installingWorker.postMessage(message);

        this.messageSent = true;
    }
}
